import { useEffect, useState, useCallback } from "react";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Users, DollarSign, Link2, Copy, TrendingUp, CreditCard, Check, MousePointer, Percent, Code, Image } from "lucide-react";

export default function AffiliatePage() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);

  const fetchData = useCallback(() => {
    api.get("/affiliate/dashboard").then((r) => setData(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    if (type === "link") { setCopiedLink(true); setTimeout(() => setCopiedLink(false), 2000); }
    else { setCopiedCode(type); setTimeout(() => setCopiedCode(null), 2000); }
    toast.success(t("affiliate.copied"));
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">{t("common.loading")}</div>;
  if (!data) return null;

  const { affiliate, referrals, commissions, payouts, banners, stats } = data;
  const baseUrl = window.location.origin;
  const trackUrl = `${baseUrl}/api/affiliate/track/${affiliate?.code}`;
  const directUrl = `${baseUrl}/register?ref=${affiliate?.code}`;

  const generateEmbedCode = (banner) => {
    return `<a href="${trackUrl}" target="_blank" rel="noopener"><img src="${banner.image_url}" width="${banner.width}" height="${banner.height}" alt="Smartpriced - Prijsmonitoring" style="border:0;" /></a>`;
  };

  const generateTextLink = () => {
    return `<a href="${trackUrl}" target="_blank" rel="noopener">Probeer Smartpriced - Intelligent prijsmonitoring voor webshops</a>`;
  };

  return (
    <div className="space-y-8" data-testid="affiliate-page">
      <div>
        <h1 className="text-2xl font-bold">{t("affiliate.portal_title")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("affiliate.promote")}</p>
      </div>

      <Card className="bg-card border-border border-l-4 border-l-emerald-500">
        <CardContent className="p-5">
          <p className="text-sm text-muted-foreground leading-relaxed">
            {t("affiliate.commission_info").replace("{pct}", stats.commission_pct)}
          </p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {[
          { label: t("affiliate.clicks"), val: stats.total_clicks, icon: MousePointer, color: "text-blue-500" },
          { label: t("affiliate.total_referrals"), val: stats.total_referrals, icon: Users, color: "text-emerald-500" },
          { label: t("affiliate.conversions"), val: stats.paid_referrals, icon: CreditCard, color: "text-amber-500" },
          { label: t("affiliate.conv_rate"), val: `${stats.conversion_rate}%`, icon: Percent, color: "text-purple-500" },
          { label: t("affiliate.total_commission"), val: `${stats.total_commission} EUR`, icon: DollarSign, color: "text-emerald-500", mono: true },
          { label: t("affiliate.pending"), val: `${stats.pending} EUR`, icon: TrendingUp, color: "text-amber-500", mono: true },
        ].map((s, i) => (
          <Card key={i} className="bg-card border-border"><CardContent className="p-4">
            <div className="flex items-center justify-between mb-1"><s.icon className={`w-4 h-4 ${s.color}`} /></div>
            <p className={`text-xl font-bold ${s.mono ? "font-mono-data" : ""}`}>{s.val}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
          </CardContent></Card>
        ))}
      </div>

      <Tabs defaultValue="links" className="w-full">
        <TabsList>
          <TabsTrigger value="links"><Link2 className="w-3.5 h-3.5 mr-1.5" />{t("affiliate.tab_links")}</TabsTrigger>
          <TabsTrigger value="referrals"><Users className="w-3.5 h-3.5 mr-1.5" />{t("affiliate.tab_referrals")} ({referrals.length})</TabsTrigger>
          <TabsTrigger value="commissions"><DollarSign className="w-3.5 h-3.5 mr-1.5" />{t("affiliate.tab_commissions")}</TabsTrigger>
        </TabsList>

        <TabsContent value="links" className="space-y-6 mt-4">
          <Card className="bg-card border-border">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">{t("affiliate.tracking_link")}</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3">
                <code className="flex-1 bg-accent/50 px-4 py-3 rounded-xl text-sm font-mono-data truncate">{trackUrl}</code>
                <Button variant="outline" onClick={() => copyToClipboard(trackUrl, "link")} data-testid="copy-track-link">
                  {copiedLink ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">{t("affiliate.tracking_info")}</p>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><Code className="w-4 h-4" />{t("affiliate.text_link")}</CardTitle></CardHeader>
            <CardContent>
              <pre className="bg-accent/50 rounded-xl p-4 text-xs font-mono-data overflow-x-auto whitespace-pre-wrap">{generateTextLink()}</pre>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => copyToClipboard(generateTextLink(), "textlink")} data-testid="copy-text-link">
                {copiedCode === "textlink" ? <><Check className="w-3.5 h-3.5 mr-1.5 text-emerald-500" />{t("affiliate.copied_short")}</> : <><Copy className="w-3.5 h-3.5 mr-1.5" />{t("affiliate.copy_code")}</>}
              </Button>
            </CardContent>
          </Card>

          {banners && banners.length > 0 && (
            <Card className="bg-card border-border">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><Image className="w-4 h-4" />{t("affiliate.banners_title")}</CardTitle></CardHeader>
              <CardContent className="space-y-6">
                <p className="text-xs text-muted-foreground">{t("affiliate.banners_desc")}</p>
                {banners.map((banner) => (
                  <div key={banner.id} className="border border-border rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{banner.name}</p>
                        <p className="text-xs text-muted-foreground">{banner.width} x {banner.height}px</p>
                      </div>
                      <Badge variant="outline">{banner.width}x{banner.height}</Badge>
                    </div>
                    <div className="bg-accent/30 rounded-lg p-3 flex items-center justify-center overflow-hidden">
                      <img src={banner.image_url} alt={banner.name} style={{ maxWidth: "100%", maxHeight: "120px" }} className="rounded" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground mb-1">{t("affiliate.embed_code")}:</p>
                      <pre className="bg-accent/50 rounded-lg p-3 text-xs font-mono-data overflow-x-auto whitespace-pre-wrap">{generateEmbedCode(banner)}</pre>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => copyToClipboard(generateEmbedCode(banner), banner.id)}>
                      {copiedCode === banner.id ? <><Check className="w-3.5 h-3.5 mr-1.5 text-emerald-500" />{t("affiliate.copied_short")}</> : <><Copy className="w-3.5 h-3.5 mr-1.5" />{t("affiliate.copy_code")}</>}
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {(!banners || banners.length === 0) && (
            <Card className="bg-card border-border">
              <CardContent className="p-8 text-center">
                <Image className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground text-sm">{t("affiliate.banners_coming")}</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="referrals" className="mt-4">
          <Card className="bg-card border-border overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead>{t("affiliate.col_user")}</TableHead><TableHead>{t("affiliate.col_date")}</TableHead><TableHead>{t("affiliate.col_status")}</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {referrals.length === 0 ? (
                  <TableRow><TableCell colSpan={3} className="text-center py-8 text-muted-foreground">{t("affiliate.no_referrals")}</TableCell></TableRow>
                ) : referrals.map((ref, i) => (
                  <TableRow key={i}>
                    <TableCell>{ref.user?.name || ref.user?.email || t("affiliate.anonymous")}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{ref.created_at ? new Date(ref.created_at).toLocaleDateString("nl-NL") : "-"}</TableCell>
                    <TableCell><Badge className={ref.has_paid ? "bg-emerald-500/20 text-emerald-400 border-0" : ""}>{ref.has_paid ? t("affiliate.paying") : t("affiliate.free")}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="commissions" className="space-y-4 mt-4">
          {commissions.length > 0 ? (
            <Card className="bg-card border-border overflow-hidden">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>{t("affiliate.col_date")}</TableHead><TableHead className="text-right">{t("affiliate.col_amount")}</TableHead><TableHead>{t("affiliate.col_status")}</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {commissions.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell className="text-sm">{new Date(c.created_at).toLocaleDateString("nl-NL")}</TableCell>
                      <TableCell className="text-right font-mono-data">{c.amount?.toFixed(2)} EUR</TableCell>
                      <TableCell><Badge className={c.paid_out ? "bg-emerald-500/20 text-emerald-400 border-0" : ""}>{c.paid_out ? t("affiliate.paid_out") : t("affiliate.outstanding")}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          ) : (
            <Card className="bg-card border-border"><CardContent className="p-8 text-center text-muted-foreground text-sm">{t("affiliate.no_commissions")}</CardContent></Card>
          )}

          {payouts.length > 0 && (
            <Card className="bg-card border-border overflow-hidden">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">{t("affiliate.payouts_title")}</CardTitle></CardHeader>
              <Table>
                <TableHeader><TableRow><TableHead>{t("affiliate.col_date")}</TableHead><TableHead className="text-right">{t("affiliate.col_amount")}</TableHead><TableHead>{t("affiliate.col_status")}</TableHead></TableRow></TableHeader>
                <TableBody>
                  {payouts.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="text-sm">{new Date(p.created_at).toLocaleDateString("nl-NL")}</TableCell>
                      <TableCell className="text-right font-mono-data">{p.amount?.toFixed(2)} EUR</TableCell>
                      <TableCell><Badge className={p.status === "paid" ? "bg-emerald-500/20 text-emerald-400 border-0" : ""}>{p.status}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
